<main>
  <header>
    <div class="row-start-center gap-3">
      <p-button icon="pi pi-arrow-left" [rounded]="true" [text]="true" severity="secondary" (click)="back()" ariaLabel="Back" />
      <h1>{{ 'Cost_Analysis_Process_Detail' | translateWF: 'Process detail' }}</h1>
    </div>
    <div class="buttons cs-button-compact">
      <p-button [label]="'Back' | translateWF: 'Back'" variant="outlined" size="small" (click)="back()" />
    </div>
  </header>

  <div class="content">
    @if (loading()) {
      <section class="loading-state">
        <i class="pi pi-spin pi-spinner"></i>
      </section>
    } @else if (context()) {
      @let ctx = context()!;
      @let colors = currentColors();

      <!-- Process row: badge + period + stage indicator -->
      <section class="process-row">
        @if (colors) {
          <span class="process-badge" [style.background-color]="colors.tint" [style.border-color]="colors.border" [style.color]="colors.dark">
            <span class="process-badge__dot" [style.background-color]="colors.light"></span>
            <span class="process-badge__label">{{ processLabels[ctx.process] }}</span>
          </span>
        }
        <span class="period-chip">{{ periodLabel() }}</span>
        @if (ctx.context.fabricCode) {
          <span class="fabric-stack">
            @if (fabricDescription()) {
              <span class="fabric-name">{{ fabricDescription() }}</span>
              <span class="fabric-code">{{ ctx.context.fabricCode }}</span>
            } @else {
              <span class="fabric-name">{{ ctx.context.fabricCode }}</span>
            }
          </span>
        }

        <!-- Stage indicator: 6 segments, current at 100% / others at 30%. -->
        <span class="stage-indicator" role="img" [attr.aria-label]="stageIndex() + 1 + ' of 6'">
          <span class="stage-indicator__track">
            @for (p of processFlow; track p; let i = $index) {
              <span class="stage-indicator__seg" [style.background-color]="processColorFor(p)" [style.opacity]="i === stageIndex() ? 1 : 0.3"></span>
            }
          </span>
          <span class="stage-indicator__label">{{ stageIndex() + 1 }} of {{ processFlow.length }}</span>
        </span>
      </section>

      @if (isEmpty()) {
        <section class="empty-card">
          <p class="empty-title">{{ 'Cost_Analysis_No_Data_For_Process' | translateWF: 'No data for this process' }}</p>
          <p class="empty-sub">
            {{
              'Cost_Analysis_No_Data_For_Process_Sub'
                | translateWF
                  : 'The selected process recorded no activity in the
        period for the chosen scope.'
            }}
          </p>
        </section>
      } @else {
        <!-- Summary metrics -->
        <section class="grid-12 mt-4">
          <div class="span-12-3">
            <app-cost-kpi-card
              icon="pi pi-dollar"
              [label]="'Cost_Analysis_Real' | translateWF: 'Real'"
              [value]="processCostPerLb() > 0 ? '$' + (processCostPerLb() | number: '1.4-4') : '—'"
              unit="USD / lb"
            />
          </div>
          <div class="span-12-3">
            <app-cost-kpi-card icon="pi pi-bolt" [label]="'Cost_Analysis_Std' | translateWF: 'Standard'" value="—" unit="USD / lb" />
          </div>
          <div class="span-12-3">
            <app-cost-kpi-card
              icon="pi pi-receipt"
              [label]="'Cost_Analysis_Total_Cost_Usd' | translateWF: 'Total cost'"
              [value]="processTotalUsd() > 0 ? '$' + (processTotalUsd() | number: '1.4-4') : '—'"
              unit="USD"
            />
          </div>
          <div class="span-12-3">
            <app-cost-kpi-card
              icon="pi pi-percentage"
              [label]="'Cost_Analysis_Pct_Of_Total' | translateWF: '% of total'"
              [value]="pctOfGrandTotal() > 0 ? (pctOfGrandTotal() | number: '1.1-1') + '%' : '—'"
              unit=""
            />
          </div>
        </section>

        <!-- Component breakdown -->
        @if (componentBreakdown().length > 0) {
          <div class="">
            <div class="flex items-baseline gap-2 mb-4">
              <h2 class="text-lg font-semibold m-0">
                {{
                  'Cost_Analysis_Component_Breakdown'
                    | translateWF
                      : 'Component
          breakdown'
                }}
              </h2>
              <span class="text-sm text-[var(--p-text-muted-color)] tabular-nums">{{ visibleBreakdown().length }} active</span>
            </div>

            <p-table [value]="visibleBreakdown()" styleClass="p-datatable-sm p-datatable-striped" [tableStyle]="{ 'min-width': '50rem' }">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ 'Cost_Analysis_Component' | translateWF: 'Component' }}</th>
                  <th class="text-right">{{ 'Cost_Analysis_Real' | translateWF: 'Real' }} (USD)</th>
                  <th class="text-right">{{ 'Cost_Analysis_Real' | translateWF: 'Real' }} ($/lb)</th>
                  <th class="text-right">{{ 'Cost_Analysis_Std' | translateWF: 'Standard' }} ($/lb)</th>
                  <th class="text-right">{{ 'Cost_Analysis_Variance' | translateWF: 'Variance' }} ($/lb)</th>
                  <th class="text-right">{{ 'Cost_Analysis_Variance_Pct' | translateWF: 'Var %' }}</th>
                  <th class="!text-right">{{ 'Cost_Analysis_Pct_Total' | translateWF: '% of process' }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr [ngClass]="{ 'opacity-50 text-[var(--p-text-muted-color)]': row.isEmpty }">
                  <td class="font-medium">{{ row.label }}</td>
                  <td class="text-right tabular-nums">{{ row.isEmpty ? '—' : '$' + (row.realCostUsd | number: '1.4-4') }}</td>
                  <td class="text-right tabular-nums">{{ row.isEmpty ? '—' : '$' + (row.realCostPerLb | number: '1.4-4') }}</td>
                  <td class="text-right tabular-nums text-[var(--p-text-muted-color)]">—</td>
                  <td class="text-right tabular-nums text-[var(--p-text-muted-color)]">—</td>
                  <td class="text-right tabular-nums text-[var(--p-text-muted-color)]">—</td>
                  <td class="text-right">
                    @if (row.isEmpty) {
                      <span class="text-[var(--p-text-muted-color)]">—</span>
                    } @else {
                      <div class="row-end-center gap-2">
                        <div class="w-24 h-2 bg-[var(--p-surface-200)] rounded-full overflow-hidden">
                          <div
                            class="h-full rounded-full transition-all duration-200"
                            [ngStyle]="{ width: row.pctOfProcess + '%' }"
                            [ngClass]="row.isDominant ? 'bg-[var(--pd-accent)]' : 'bg-[var(--pd-accent-soft)]'"
                          ></div>
                        </div>
                        <span class="tabular-nums font-medium w-12 text-right">{{ row.pctOfProcess | number: '1.1-1' }}%</span>
                      </div>
                    }
                  </td>
                </tr>
              </ng-template>
              <ng-template pTemplate="footer">
                <tr>
                  <td class="font-medium">{{ 'Cost_Analysis_Total' | translateWF: 'Process total' }}</td>
                  <td class="text-right tabular-nums font-medium">${{ processTotalUsd() | number: '1.4-4' }}</td>
                  <td class="text-right tabular-nums font-medium">${{ processCostPerLb() | number: '1.4-4' }}</td>
                  <td class="text-right text-[var(--p-text-muted-color)]">—</td>
                  <td class="text-right text-[var(--p-text-muted-color)]">—</td>
                  <td class="text-right text-[var(--p-text-muted-color)]">—</td>
                  <td class="!text-right tabular-nums font-medium">100.0%</td>
                </tr>
              </ng-template>
            </p-table>

            <!-- Inactive-components affordance (collapsed by default) -->
            @if (hiddenEmptyCount() > 0) {
              <div class="inactive-row">
                <p class="m-0">
                  <span class="tabular">{{ hiddenEmptyCount() }}</span>
                  {{ 'Cost_Analysis_Components_Inactive' | translateWF: 'components inactive for this process' }}
                </p>
                <button type="button" class="inline-action" style="display: block; margin: 4px auto 0" (click)="toggleShowAllEmpty()">
                  @if (showAllEmpty()) {
                    {{ 'Cost_Analysis_Hide_Inactive' | translateWF: 'hide' }}
                  } @else {
                    {{ 'Cost_Analysis_Show_All_Inactive' | translateWF: 'show all' }}
                  }
                </button>
              </div>
            }
          </div>
        }

        <!-- Distribution -->
        @if (distribution().length > 0) {
          <div class="">
            <div class="flex items-baseline gap-2 mb-4">
              <h2 class="text-lg font-semibold m-0">{{ 'Cost_Analysis_Distribution' | translateWF: 'Distribution' }}</h2>
              <span class="text-sm text-[var(--p-text-muted-color)] tabular-nums">{{ distribution().length }} {{ distribution().length === 1 ? 'item' : 'items' }}</span>
            </div>

            <p-table [value]="distribution()" styleClass="p-datatable-sm p-datatable-striped" [tableStyle]="{ 'min-width': '50rem' }">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ 'Cost_Analysis_Identifier' | translateWF: 'Identifier' }}</th>
                  <th class="text-right">{{ 'Cost_Analysis_Lbs' | translateWF: 'Lbs' }}</th>
                  <th class="text-right">{{ 'Cost_Analysis_Weight_Pct' | translateWF: 'Weight %' }}</th>
                  <th class="text-right">{{ 'Cost_Analysis_Real' | translateWF: 'Real' }} (USD)</th>
                  <th class="text-right">{{ 'Cost_Analysis_Real' | translateWF: 'Real' }} ($/lb)</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>
                    <div class="row-start-center gap-1">
                      <span class="font-medium text-[var(--p-text-color)]">{{ row.label }}</span>
                      <span class="row-sm-start-center flex-wrap mt-1">
                        <p-tag [value]="row.level" severity="secondary" [rounded]="true" />
                        @switch (row.costSourceTag) {
                          @case ('real') {
                            <p-tag [value]="'Cost_Analysis_Real' | translateWF: 'Real'" severity="success" [rounded]="true" />
                          }
                          @case ('standard') {
                            <p-tag [value]="'Cost_Analysis_Std' | translateWF: 'Standard'" severity="warn" [rounded]="true" />
                          }
                          @case ('mixed') {
                            <p-tag [value]="'Cost_Analysis_Source_Mixed' | translateWF: 'Mixed'" severity="info" [rounded]="true" />
                          }
                        }
                        @if (rowColor(row); as color) {
                          @switch (color.kind) {
                            @case ('colored') {
                              <span class="text-xs text-[var(--p-text-muted-color)] row-start-center gap-1">
                                <span>{{ color.name }}</span>
                                @if (color.labDipNumber) {
                                  <span class="font-mono">· {{ color.labDipNumber }}</span>
                                }
                              </span>
                            }
                            @case ('uncolored') {
                              <span class="text-xs text-[var(--p-text-muted-color)]">
                                {{ 'Cost_Analysis_Color_Uncolored' | translateWF: 'Uncolored / Pending' }}
                              </span>
                            }
                            @case ('orphan') {
                              <span class="text-xs text-orange-600 font-mono">
                                {{ color.diagnostic }}
                              </span>
                            }
                          }
                        }
                      </span>
                    </div>
                  </td>
                  <td class="text-right tabular-nums">{{ row.weightLbs | number: '1.0-0' }}</td>
                  <td class="text-right tabular-nums">{{ row.weightPct | number: '1.1-1' }}%</td>
                  <td class="text-right tabular-nums">${{ row.realCostUsd | number: '1.4-4' }}</td>
                  <td class="text-right tabular-nums">${{ row.realCostPerLb | number: '1.4-4' }}</td>
                </tr>
              </ng-template>
            </p-table>
          </div>
        }
      }
    } @else {
      <section class="empty-card">
        <p>{{ 'Cost_Analysis_No_Context' | translateWF: 'No context selected. Return to the dashboard and pick a process.' }}</p>
      </section>
    }
  </div>
</main>
